/**
 * Converts Excel-style serial date (days since 1900-01-01) to CRM-friendly strings.
 * Used when syncing spreadsheet rows to HubSpot/Salesforce so date/datetime fields
 * receive ISO format instead of raw serial numbers.
 */

/** Excel serial 1 = 1900-01-01. 1970-01-01 UTC = serial 25569. */
const EXCEL_EPOCH_OFFSET = 25569;
const MS_PER_DAY = 86400 * 1000;

/** Reasonable Excel date range: 1900-01-01 (1) through 9999 (serial ~2958465). */
const MIN_SERIAL = 1;
const MAX_SERIAL = 2958466;

/**
 * Returns true if the value looks like an Excel serial date (numeric and in range).
 */
function isExcelSerial(value: unknown): value is number {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= MIN_SERIAL && value <= MAX_SERIAL;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return false;
    const n = Number(trimmed);
    return Number.isFinite(n) && n >= MIN_SERIAL && n <= MAX_SERIAL;
  }
  return false;
}

/**
 * Convert Excel serial to a Date (UTC). Fractional part is time of day.
 */
function excelSerialToDate(serial: number): Date {
  const days = Math.floor(serial);
  const fraction = serial - days;
  const utcMs =
    (days - EXCEL_EPOCH_OFFSET) * MS_PER_DAY + fraction * MS_PER_DAY;
  return new Date(utcMs);
}

/**
 * Normalize value to a number for conversion. Returns NaN if not parseable.
 */
function toSerialNumber(value: string | number): number {
  if (typeof value === 'number') return value;
  const trimmed = String(value).trim();
  if (trimmed === '') return NaN;
  return Number(trimmed);
}

/**
 * If value looks like an Excel serial date, convert to ISO date string (YYYY-MM-DD).
 * Otherwise return the original value (string or number).
 */
export function formatValueForCrmDate(
  value: string | number | null | undefined
): string | number {
  if (value == null || value === '') return value ?? '';
  if (!isExcelSerial(value)) return value;
  const serial = toSerialNumber(value as string | number);
  if (!Number.isFinite(serial)) return value;
  const date = excelSerialToDate(serial);
  return date.toISOString().slice(0, 10);
}

/**
 * If value looks like an Excel serial date, convert to ISO datetime string.
 * Otherwise return the original value (string or number).
 */
export function formatValueForCrmDatetime(
  value: string | number | null | undefined
): string | number {
  if (value == null || value === '') return value ?? '';
  if (!isExcelSerial(value)) return value;
  const serial = toSerialNumber(value as string | number);
  if (!Number.isFinite(serial)) return value;
  const date = excelSerialToDate(serial);
  return date.toISOString();
}

function isEmptyCell(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
}

/**
 * Return a copy of rows where any cell that is completely empty (null, undefined,
 * '' or whitespace-only) is omitted from the row object. The backend should only
 * validate and sync cells that are present; empty cells are never sent.
 */
export function rowsWithEmptyCellsOmitted(
  rows: Record<string, unknown>[]
): Record<string, unknown>[] {
  return rows.map((row) => {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(row)) {
      if (!isEmptyCell(v)) out[k] = v;
    }
    return out;
  });
}
